import React, { useEffect, useRef, useState } from "react";
import styles from "./PresenceIconWithTooltip.module.scss";
import { theSharedBrowser } from "../../helpers/helpers";
import { IContactContainer } from "../../interfaces/interfaces";
import { IConfigCD } from "../../interfaces/interfacesConfig";
import { TimeoutOrNullT } from "../../interfaces/types";
import { ICombiPic } from "../contactDetailsComponent/models/ModelCombinedContact";
import { ICustomerLabels } from "../contactDetailsComponent/models/ModelContentData";
import { PresenceTooltip } from "../presenceTooltip/PresenceTooltip";
import UserPresenceIcon from "../userPresenceIcon/UserPresenceIcon";
import { Box } from "@mui/material";
import { IAsnAppointmentEntry } from "../../interfaces/serializable/IAsnAppointmentEntry";
import { IAppointmentEntryEX } from "../../interfaces/IAppointmentEntryEX";
import { IAsnLocationInformation2 } from "../../interfaces/serializable/IAsnLocationInformation2";

interface IProps {
	id?: string;
    contact: IContactContainer | undefined;
    customLabels: ICustomerLabels;
    contactDetails: IConfigCD;
    combiPic: ICombiPic | undefined;
	currentAppointments: IAppointmentEntryEX[];
	nextAppointment: string;
    size?: number;
    presenceTooltipDelay?: number;
    pttShouldBeClosed?: boolean;
    triggerPTT?: boolean;
	onToolTipClosed?: () => void;
    mouseHover: () => void;
	// the location information of the selected line of the logged-in user
	// to compare against the listed phone numbers (needed to display the short version of a number)
	locationInformation?: IAsnLocationInformation2;
}

export const PresenceIconWithTooltip = (props: IProps) => {
	const [showPresenceTooltipMenu, setShowPresenceTooltipMenu] = useState<boolean | undefined>();
	const [cursorOverPTT, setCursorOverPTT] = useState<boolean>(false);
	const enterUPITimeout = useRef<TimeoutOrNullT>(null);
	const leaveUPITimeout = useRef<TimeoutOrNullT>(null);
	const anchorEl = useRef<HTMLElement | null>(null);
	const presenceTooltipDelay: number = props.presenceTooltipDelay ?? 300;

	useEffect(() => {
		if (showPresenceTooltipMenu === false && props.onToolTipClosed)
			props.onToolTipClosed();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showPresenceTooltipMenu]);

	useEffect(() => {
		return () => {
			if (leaveUPITimeout.current !== null)
				clearTimeout(leaveUPITimeout.current);
			if (enterUPITimeout.current)
				clearTimeout(enterUPITimeout.current);
		};
	}, []);

	useEffect(() => {
		if (props.pttShouldBeClosed) {
			setCursorOverPTT(false);
			setShowPresenceTooltipMenu(false);
		}
	}, [props.pttShouldBeClosed]);

	useEffect(() => {
		if (props.triggerPTT === true)
			setShowPresenceTooltipMenu(true);
	}, [props.triggerPTT]);

	const appointmentEntry: IAsnAppointmentEntry = {
		u8sSubject: props.contact?.nextAppointment?.u8sSubject || "",
		stStart: props.contact?.nextAppointment?.stStart ?? "",
		stEnd: props.contact?.nextAppointment?.stEnd ?? "",
		bAllDay: props.contact?.nextAppointment?.bAllDay ?? false,
		bIsValid: props.contact?.nextAppointment?.bIsValid ?? false,
		Location: props.contact?.nextAppointment?.Location,
		iDuration: 0,
		iImportance: 0,
		iSensitivity: 0,
		iStatus: 0,
		u8sCategory: "",
		u8sDataSource: "",
		u8sEntryID: "",
		u8sOwner: "",
		u8sText: ""
	};

	const tooltipContact: IContactContainer = {
		presence: props.contact?.presence,
		asnNetDatabaseContact: props.contact?.asnNetDatabaseContact,
		contactID: props.contact?.asnNetDatabaseContact?.u8sSIPAddress || "",
		nextAppointment: appointmentEntry,
		currentAppointments: props.contact?.currentAppointments,
		customNote: props.contact?.customNote,
		absentState: props.contact?.absentState,
		agentState: props.contact?.agentState,
		isMobileAvailable: props.contact?.isMobileAvailable,
		msTeamsEmail: props.contact?.msTeamsEmail,
		clientCapabilities: props.contact?.clientCapabilities,
		seqPhoneLines: props.contact?.seqPhoneLines,
		seqLineForwards: props.contact?.seqLineForwards,
		seqCalls: props.contact?.seqCalls
	};

	const mouseOverUserPresence = (): void => {
		props.mouseHover();
		if (tooltipContact.contactID === "")
			return;
		if (enterUPITimeout.current)
			clearTimeout(enterUPITimeout.current);
		enterUPITimeout.current = setTimeout(() => {
			if (!props.pttShouldBeClosed)
				setShowPresenceTooltipMenu(true);
		}, presenceTooltipDelay);
	};

	const mouseLeaveUserPresence = (): void => {
		if (leaveUPITimeout.current !== null)
			clearTimeout(leaveUPITimeout.current);
		if (enterUPITimeout.current)
			clearTimeout(enterUPITimeout.current);

		leaveUPITimeout.current = setTimeout(() => {
			if (!cursorOverPTT)
				setShowPresenceTooltipMenu(false);
		}, 100);
	};

	const closePTT = (): void => {
		setCursorOverPTT(false);
		setShowPresenceTooltipMenu(false);
		if (enterUPITimeout.current)
			clearTimeout(enterUPITimeout.current);
	};

	return (
		<>
			<Box
				onClick={(e) => {
					if (theSharedBrowser.is.touchScreen) {
						e.preventDefault();
						e.stopPropagation();
						setShowPresenceTooltipMenu(!showPresenceTooltipMenu);
					}
				}}
				onMouseEnter={() => { mouseOverUserPresence(); }}
				onMouseLeave={() => { mouseLeaveUserPresence(); }}
			>
				<UserPresenceIcon
					pict={props.combiPic}
					pres={props.contact?.presence || 0}
					unknownPresence={props.contact?.presence === undefined}
					size={props.size}
				/>
			</Box>

			{showPresenceTooltipMenu || cursorOverPTT
				? <PresenceTooltip
					ref={anchorEl}
					contact={tooltipContact}
					customLabels={props.customLabels.CustLabels}
					configCD={props.contactDetails.ContactDetails}
					nextAppointment={props.nextAppointment}
					currentAppointments={props.currentAppointments}
					locationInformation={props.locationInformation}
					mouseIsOver={(isOver) => {
						setCursorOverPTT(isOver);
					}}
					close={() => { closePTT(); }}
				/>
				: null
			}
			<span className={styles.anchorForPresenceTooltip} data-id="pptRef" ref={anchorEl} />
		</>
	);
};
